import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import {
  TicketCategory,
  TicketPipeline,
  TicketPriority,
  TicketSlaAppliesWhen,
  TicketSlaBusinessHours,
  TicketSlaPolicy
} from '../../../models/tickets-config.model';
import { buildCategoryTree, categoryIndent, getOrigenLabel } from '../../../utils/ticket-format.util';

/** Fila editable de horario laboral por día. day: 0=domingo..6=sábado. */
interface BusinessDayRow {
  day: number;
  label: string;
  enabled: boolean;
  start: string;
  end: string;
}

/** Opción plana de categoría (con indentación por jerarquía). */
interface CategoryOption {
  id: number;
  label: string;
}

@Component({
  selector: 'app-tickets-sla-policies-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ButtonComponent],
  templateUrl: './sla-policies-form.component.html'
})
export class TicketsSlaPoliciesFormComponent implements OnInit, OnDestroy {
  @Output() saved = new EventEmitter<TicketSlaPolicy>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private toast = inject(ToastService);

  isEditing = false;
  itemId: number | null = null;
  saving = false;
  error: string | null = null;
  private originalName = '';

  // Catálogos para las condiciones de activación (appliesWhen).
  priorities: TicketPriority[] = [];
  categoryOptions: CategoryOption[] = [];
  pipelines: TicketPipeline[] = [];
  readonly sources = ['MANUAL', 'EMAIL', 'CHAT', 'PHONE', 'WEB_FORM', 'PORTAL', 'SOCIAL', 'SUB_TICKET', 'SPLIT', 'API'];
  readonly getOrigenLabel = getOrigenLabel;

  // Selecciones de appliesWhen.
  selectedPriorities: number[] = [];
  selectedCategories: number[] = [];
  selectedPipelines: number[] = [];
  selectedSources: string[] = [];

  // Horario laboral (businessHours). Orden de visualización Lun..Dom.
  businessHoursDays: BusinessDayRow[] = this.buildDefaultDays();

  // Días festivos (holidays) como lista de "YYYY-MM-DD".
  holidays: string[] = [];
  newHoliday = '';

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
    description: [''],
    sortOrder: [0],
    isDefault: [false],
    isActive: [true],
    timezone: ['']
  });

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.loadCatalogs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCatalogs(): void {
    this.configApi.getPriorities()
      .pipe(catchError(() => of([] as TicketPriority[])), takeUntil(this.destroy$))
      .subscribe((p) => { this.priorities = p.sort((a, b) => a.level - b.level); });

    this.configApi.getCategories()
      .pipe(catchError(() => of([] as TicketCategory[])), takeUntil(this.destroy$))
      .subscribe((cats) => {
        this.categoryOptions = buildCategoryTree(cats).map((n) => ({
          id: n.id,
          label: categoryIndent(n.depth) + n.name
        }));
      });

    this.configApi.getPipelines()
      .pipe(catchError(() => of([] as TicketPipeline[])), takeUntil(this.destroy$))
      .subscribe((p) => { this.pipelines = p.sort((a, b) => a.sortOrder - b.sortOrder); });
  }

  private buildDefaultDays(): BusinessDayRow[] {
    return [
      { day: 1, label: 'Lunes', enabled: false, start: '09:00', end: '18:00' },
      { day: 2, label: 'Martes', enabled: false, start: '09:00', end: '18:00' },
      { day: 3, label: 'Miércoles', enabled: false, start: '09:00', end: '18:00' },
      { day: 4, label: 'Jueves', enabled: false, start: '09:00', end: '18:00' },
      { day: 5, label: 'Viernes', enabled: false, start: '09:00', end: '18:00' },
      { day: 6, label: 'Sábado', enabled: false, start: '09:00', end: '14:00' },
      { day: 0, label: 'Domingo', enabled: false, start: '09:00', end: '14:00' }
    ];
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = null;
    this.saving = false;
    this.selectedPriorities = [];
    this.selectedCategories = [];
    this.selectedPipelines = [];
    this.selectedSources = [];
    this.businessHoursDays = this.buildDefaultDays();
    this.holidays = [];
    this.newHoliday = '';
    this.form.reset({ name: '', description: '', sortOrder: 0, isDefault: false, isActive: true, timezone: '' });
  }

  loadPolicy(id: number): void {
    this.isEditing = true;
    this.itemId = id;
    this.configApi.getSlaPolicyById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          description: item.description ?? '',
          sortOrder: item.sortOrder ?? 0,
          isDefault: item.isDefault,
          isActive: item.isActive
        });
        this.originalName = item.name;
        this.parseAppliesWhen(item.appliesWhen);
        this.parseBusinessHours(item.businessHours);
        this.parseHolidays(item.holidays);
      },
      error: (err) => { this.error = err.error?.message || 'Error al cargar política SLA'; }
    });
  }

  // ── Parsing de los JSON existentes al editar ──────────────────────────────

  private asJson(v: unknown): unknown {
    if (typeof v === 'string') {
      try { return JSON.parse(v); } catch { return null; }
    }
    return v ?? null;
  }

  private parseAppliesWhen(raw: unknown): void {
    const aw = this.asJson(raw) as TicketSlaAppliesWhen | null;
    if (!aw || typeof aw !== 'object') return;
    this.selectedPriorities = Array.isArray(aw.priorities) ? aw.priorities.map(Number) : [];
    this.selectedCategories = Array.isArray(aw.categories) ? aw.categories.map(Number) : [];
    this.selectedPipelines = Array.isArray(aw.pipelines) ? aw.pipelines.map(Number) : [];
    this.selectedSources = Array.isArray(aw.sources) ? aw.sources.map(String) : [];
  }

  private parseBusinessHours(raw: unknown): void {
    const bh = this.asJson(raw) as TicketSlaBusinessHours | null;
    if (!bh || typeof bh !== 'object') return;
    if (bh.timezone) this.form.patchValue({ timezone: bh.timezone });
    if (Array.isArray(bh.days)) {
      for (const d of bh.days) {
        const slot = this.businessHoursDays.find((x) => x.day === Number(d.day));
        if (slot) {
          slot.enabled = true;
          if (d.start) slot.start = d.start;
          if (d.end) slot.end = d.end;
        }
      }
    }
  }

  private parseHolidays(raw: unknown): void {
    const h = this.asJson(raw);
    if (Array.isArray(h)) {
      this.holidays = h.filter((x): x is string => typeof x === 'string').sort();
    }
  }

  // ── Toggles de selección (appliesWhen) ────────────────────────────────────

  togglePriority(id: number): void { this.toggleNum(this.selectedPriorities, id); }
  toggleCategory(id: number): void { this.toggleNum(this.selectedCategories, id); }
  togglePipeline(id: number): void { this.toggleNum(this.selectedPipelines, id); }
  toggleSource(src: string): void {
    const i = this.selectedSources.indexOf(src);
    if (i >= 0) this.selectedSources.splice(i, 1); else this.selectedSources.push(src);
  }

  private toggleNum(arr: number[], id: number): void {
    const i = arr.indexOf(id);
    if (i >= 0) arr.splice(i, 1); else arr.push(id);
  }

  // ── Días festivos ─────────────────────────────────────────────────────────

  addHoliday(): void {
    const v = (this.newHoliday || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return;
    if (!this.holidays.includes(v)) {
      this.holidays = [...this.holidays, v].sort();
    }
    this.newHoliday = '';
  }

  removeHoliday(date: string): void {
    this.holidays = this.holidays.filter((h) => h !== date);
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  // ── Construcción de los JSON al guardar ───────────────────────────────────

  private buildAppliesWhen(): TicketSlaAppliesWhen {
    const aw: TicketSlaAppliesWhen = {};
    if (this.selectedPriorities.length) aw.priorities = [...this.selectedPriorities];
    if (this.selectedCategories.length) aw.categories = [...this.selectedCategories];
    if (this.selectedPipelines.length) aw.pipelines = [...this.selectedPipelines];
    if (this.selectedSources.length) aw.sources = [...this.selectedSources];
    return aw;
  }

  private buildBusinessHours(): TicketSlaBusinessHours {
    const days = this.businessHoursDays
      .filter((d) => d.enabled)
      .map((d) => ({ day: d.day, start: d.start, end: d.end }));
    const tz = (this.form.value.timezone || '').trim();
    const bh: TicketSlaBusinessHours = {};
    if (tz) bh.timezone = tz;
    if (days.length) bh.days = days;
    return bh; // {} = 24/7
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    this.error = null;
    const raw = this.form.value;
    const req = {
      name: raw.name,
      description: raw.description || undefined,
      sortOrder: Number(raw.sortOrder ?? 0),
      isDefault: !!raw.isDefault,
      isActive: !!raw.isActive,
      appliesWhen: this.buildAppliesWhen(),
      businessHours: this.buildBusinessHours(),
      holidays: [...this.holidays]
    };
    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updateSlaPolicy(this.itemId, req)
      : this.configApi.createSlaPolicy(req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Política SLA',
          this.originalName !== (raw.name?.trim() || '')
            ? `Se actualizó "${this.originalName}" → "${raw.name?.trim() || ''}".`
            : `Se actualizó "${raw.name?.trim() || ''}" correctamente.`);
        else this.toast.created('Política SLA', `Se creó "${raw.name?.trim() || ''}" correctamente.`);
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar la política SLA';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void { this.cancelled.emit(); }
}
